import { ReactFramework } from "@/utils/templateProjects";
import { Storyboard } from "@/utils/types";
import { LayoutPreset } from "./hooks/useLayoutPresets";
import { TypographyPreset } from "./hooks/useTypographyPresets";
import { v4 as uuidv4 } from "uuid";

export const sanitizeComponentName = (name: string) => {
  // Remove all non-alphanumeric characters, split on spaces, capitalize first letter of each word
  // - Preserves mixed-case words (e.g., "DiamondBox" stays "DiamondBox")
  // - Converts all-lowercase words to capitalized (e.g., "button" → "Button")
  // - Converts all-uppercase words to capitalized (e.g., "BUTTON" → "Button")
  const processedName = name
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .replace(/^[0-9]+/, "")
    .split(/\s+/)
    .map((word) => {
      if (!word) return "";
      const isAllLowercase = word === word.toLowerCase();
      const isAllUppercase = word === word.toUpperCase();
      // If word is all lowercase or all uppercase, convert to Title Case
      // Otherwise preserve the original casing (for mixed case like "DiamondBox")
      if (isAllLowercase || isAllUppercase) {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
      return word;
    })
    .join("");

  return processedName;
};

export const createDummyComponent = (
  staticContents: string,
  framework: ReactFramework,
  componentName?: string,
): string => {
  const sanitizedComponentName = sanitizeComponentName(
    componentName || "DummyComponent",
  );

  if (framework === ReactFramework.EXPO) {
    return `import React from "react";
import { View } from "react-native";

const ${sanitizedComponentName} = () => {
  return ${staticContents};
};

export default ${sanitizedComponentName};`;
  }

  if (framework === ReactFramework.NEXT_JS_APP_ROUTER) {
    return `"use client";
    
import React from "react";

const ${sanitizedComponentName} = () => {
  return ${staticContents};
};
  
export default ${sanitizedComponentName};`;
  }

  return `import React from "react";

const ${sanitizedComponentName} = () => {
  return ${staticContents};
};
  
export default ${sanitizedComponentName};`;
};

export const wrapWithBlankStoryboard = (
  staticContents: string,
  framework: ReactFramework,
  fixedWidth: number | "FULL",
  fixedHeight: number | "FULL",
): string => {
  if (framework === ReactFramework.EXPO) {
    return `<View className="w-${fixedWidth === "FULL" ? "full" : `[${fixedWidth}px]`} h-${fixedHeight === "FULL" ? "full" : `[${fixedHeight}px]`} bg-white">${staticContents}</View>`;
  }

  return `<div className="w-${fixedWidth === "FULL" ? "full" : `[${fixedWidth}px]`} h-${fixedHeight === "FULL" ? "full" : `[${fixedHeight}px]`} bg-white">${staticContents}</div>`;
};

export const wrapWithBlankStoryboardNoTailwind = (
  staticContents: string,
  framework: ReactFramework,
  fixedWidth: number | "FULL",
  fixedHeight: number | "FULL",
): string => {
  if (framework === ReactFramework.EXPO) {
    return `<View style="width: ${fixedWidth === "FULL" ? "100%" : `[${fixedWidth}px]`}; height: ${fixedHeight === "FULL" ? "100%" : `[${fixedHeight}px]`}; background-color: #fff;">${staticContents}</View>`;
  }

  return `<div style="width: ${fixedWidth === "FULL" ? "100%" : `[${fixedWidth}px]`}; height: ${fixedHeight === "FULL" ? "100%" : `[${fixedHeight}px]`}; background-color: #fff;">${staticContents}</div>`;
};

export const getInstantUpdateHtmlForStoryboard = (
  isCursorText: boolean,
  usingTailwind: boolean,
  selectedTypographyOptionToUse: TypographyPreset,
  selectedLayoutOptionToUse: LayoutPreset,
  project: any,
  fixedWidth: number | "FULL",
  fixedHeight: number | "FULL",
  fixedCodebaseIdForHtml?: string,
  darkText?: boolean,
) => {
  let fixedCodebaseIdToUse = fixedCodebaseIdForHtml;
  if (!fixedCodebaseIdToUse) {
    fixedCodebaseIdToUse = `tempo-${uuidv4()}`;
  }

  /*
   * JSX Contents - this is the code that will be inserted into the codebase
   */

  let jsxContents: string | undefined = undefined;
  if (isCursorText) {
    // Text
    // Explicit height for text but not width
    const customClasses: string[] = [];
    if (fixedHeight === "FULL") {
      customClasses.push("h-full");
    } else {
      customClasses.push(`h-[${fixedHeight}px]`);
    }

    if (darkText) {
      customClasses.push(" text-black");
    } else {
      customClasses.push(" text-white");
    }

    jsxContents = selectedTypographyOptionToUse.getJsxWithCustomClasses?.(
      customClasses.join(" "),
      fixedCodebaseIdToUse,
    );
  } else if (!usingTailwind) {
    // No tailwind
    const widthStyle =
      fixedWidth === "FULL" ? "width: 100%;" : `width: ${fixedWidth}px;`;
    const heightStyle =
      fixedHeight === "FULL" ? "height: 100%;" : `height: ${fixedHeight}px;`;

    jsxContents = selectedLayoutOptionToUse.getHtmlNoTailwind(
      `${widthStyle} ${heightStyle}`,
      fixedCodebaseIdToUse,
    );
  } else {
    // Layout with tailwind
    const customClasses: string[] = [];
    if (fixedWidth === "FULL") {
      customClasses.push("w-full");
    } else {
      customClasses.push(`w-[${fixedWidth}px]`);
    }
    if (fixedHeight === "FULL") {
      customClasses.push("h-full");
    } else {
      customClasses.push(`h-[${fixedHeight}px]`);
    }

    if (project.framework === ReactFramework.EXPO) {
      jsxContents =
        selectedLayoutOptionToUse.getExpoJsx?.(
          customClasses.join(" "),
          fixedCodebaseIdToUse,
        ) || "";
    } else {
      jsxContents =
        selectedLayoutOptionToUse.getJsxWithCustomClasses?.(
          customClasses.join(" "),
          fixedCodebaseIdToUse,
        ) || "";
    }
  }

  /*
   * Instant Update HTML - this will be inserted into the iframe for an instant update
   */
  let instantUpdateHtml;

  if (isCursorText) {
    // Text
    const customStyles: string[] = [];
    if (darkText) {
      customStyles.push("color: black;");
    } else {
      customStyles.push("color: white;");
    }
    if (fixedHeight === "FULL") {
      customStyles.push("height: 100%;");
    } else {
      customStyles.push(`height: ${fixedHeight}px;`);
    }

    instantUpdateHtml = selectedTypographyOptionToUse.getHtmlWithCustomStyles(
      customStyles.join(" "),
      fixedCodebaseIdToUse,
    );
  } else {
    // Layout
    const customStyles: string[] = [];
    if (fixedWidth === "FULL") {
      customStyles.push("width: 100%;");
    } else {
      customStyles.push(`width: ${fixedWidth}px;`);
    }
    if (fixedHeight === "FULL") {
      customStyles.push("height: 100%;");
    } else {
      customStyles.push(`height: ${fixedHeight}px;`);
    }
    instantUpdateHtml = selectedLayoutOptionToUse.getHtmlWithCustomStyles(
      customStyles.join(" "),
      fixedCodebaseIdToUse,
    );
  }

  return { instantUpdateHtml, jsxContents };
};
